/**
 * bracketBuilder.js — تبدیل مسابقات UWW به درخت براکت برای کتابخانه vue-tournament-bracket
 *
 * استراتژی اتصال بازی‌ها (ساخت درخت دقیق):
 *  ۱) جریان برنده (دقیق): برندهٔ هر بازی، در بازی بعدی حاضر است → لینک child.next = parent
 *  ۲) فالبک موقعیتی: شماره‌های بازی UWW در هر راند ترتیب براکتی دارند (جفت‌های متوالی
 *     به راند بعد می‌روند) → وقتی جریان برنده در دسترس نیست (راندهای هنوز برگزارنشده)،
 *     بازی‌های مرتب‌شده با شماره، جفتی به والدِ بعدی وصل می‌شوند.
 *  ۳) بازی‌های بی‌اتصال (مثل مقدماتیِ جفت‌نشدن با نسبت ۲:۱) → در «دیدارهای جدا» برگردانده می‌شوند.
 *
 * خروجی سازگار با فرمت flatTree کتابخانه است:
 *   هر بازی: { id, next, player1, player2, ...متادیتا }
 *   ریشه: بازی بدون next (فینال طلا برای درخت اصلی، دیدار برنز برای درخت‌های برنز)
 */

// ترتیب جریان راندها (رپشاژ بعد از نیمه‌نهایی وارد رده‌بندی برنز می‌شود)
const ROUND_RANK = {
    'Qualif.': 0,
    '1/16 Final': 1,
    '1/8 Final': 2,
    '1/4 Final': 3,
    '1/2 Final': 4,
    'Repechage': 5,
    'Final 3-5': 6,
    'Final 1-2': 7,
};

const MAIN_FLOW = ['Qualif.', '1/16 Final', '1/8 Final', '1/4 Final', '1/2 Final', 'Final 1-2'];

/** کلید پایدار هویت بازیکن (یکسان در همهٔ راندها) */
function playerKey(side) {
    if (!side) return null;
    if (side.athleteId) return 'A:' + side.athleteId;
    if (side.name) return 'N:' + side.name.trim().toLowerCase();
    return null;
}

/** کلید برندهٔ بازی (در صورت تکمیل) */
function winnerKeyOf(m) {
    if (!m || !m.isCompleted) return null;
    const w = m.red && m.red.isWinner ? m.red : (m.blue && m.blue.isWinner ? m.blue : null);
    return w ? playerKey(w) : null;
}

/** تبدیل یک سمت مسابقه به آبجکت player کتابخانه */
function toLibPlayer(side, match, slot) {
    if (!side || side.tbd || (!side.name && !side.nameFa)) {
        return {
            id: 'tbd-' + match.id + '-' + slot,
            name: '؟',
            nameEn: '',
            flagUrl: '',
            photo: '',
            countryFa: '',
            code: '',
            points: null,
            isIran: false,
            winner: null,
            tbd: true,
            match,
        };
    }
    return {
        // شناسهٔ پایدار برای هایلایت هم‌مسیر کتابخانه (hover یک کشتی‌گیر در کل درخت)
        id: playerKey(side) || ('tbd-' + match.id + '-' + slot),
        name: side.nameFa || side.name,
        nameEn: side.name || '',
        flagUrl: side.flagUrl || '',
        photo: side.photo || '',
        countryFa: side.countryFa || '',
        code: side.code || '',
        points: side.points !== undefined ? side.points : null,
        isIran: !!side.isIran,
        winner: match.isCompleted ? !!side.isWinner : null,
        tbd: false,
        match,
    };
}

/**
 * ساخت نقشهٔ next: شناسهٔ بازی → شناسهٔ بازی والد (بازی که برندهٔ فعلی در آن حضور دارد)
 * ۱) جریان برنده؛ ۲) فالبک موقعیتی برای راندهای بی‌نتیجه
 */
function buildNextMap(matches) {
    const nextOf = new Map();

    // ایندکس حضور: کلید بازیکن → بازی‌هایی که در آن‌هاست
    const appearance = new Map();
    matches.forEach(m => {
        [m.red, m.blue].forEach(side => {
            const k = playerKey(side);
            if (!k) return;
            if (!appearance.has(k)) appearance.set(k, []);
            appearance.get(k).push(m);
        });
    });

    // ۱) جریان برنده — نزدیک‌ترین بازیِ بعدی که برنده در آن حاضر است
    matches.forEach(m => {
        if (!m.isCompleted) return;
        const k = winnerKeyOf(m);
        if (!k) return;
        const rankM = ROUND_RANK[m.round] !== undefined ? ROUND_RANK[m.round] : 99;
        let best = null;
        (appearance.get(k) || []).forEach(h => {
            if (!h || h.id === m.id) return;
            const rankH = ROUND_RANK[h.round] !== undefined ? ROUND_RANK[h.round] : 99;
            if (rankH > rankM && (!best || rankH < best.rank)) {
                best = { id: h.id, rank: rankH };
            }
        });
        if (best) nextOf.set(m.id, best.id);
    });

    // ۲) فالبک موقعیتی — راندهای متوالی جریان اصلی که هنوز نتیجه ندارند
    for (let i = 0; i + 1 < MAIN_FLOW.length; i++) {
        const prevRound = MAIN_FLOW[i];
        const nextRound = MAIN_FLOW[i + 1];
        const prevGames = matches.filter(m => m.round === prevRound);
        const nextGames = matches.filter(m => m.round === nextRound);
        if (!prevGames.length || !nextGames.length) continue;

        const pool = prevGames
            .filter(m => !nextOf.has(m.id))
            .sort((a, b) => (a.number || 0) - (b.number || 0));
        if (!pool.length) continue;

        // فقط وقتی نسبت ۲:۱ برقرار است جفت‌سازی موقعیتی معتبر است
        const expected = Math.floor(prevGames.length / 2);
        if (nextGames.length !== expected) continue;

        const parents = nextGames
            .sort((a, b) => (a.number || 0) - (b.number || 0))
            .filter(p => (childrenCount(nextOf, p.id) < 2));

        let idx = 0;
        parents.forEach(parent => {
            [0, 1].forEach(() => {
                if (idx >= pool.length) return;
                const child = pool[idx++];
                if (child.id === parent.id) return;
                nextOf.set(child.id, parent.id);
            });
        });
    }

    // ۳) فالبک تشک‌محور — وقتی نسبت ۲:۱ برقرار نیست (مثل مقدماتی ۱۰ → یک‌هشتم ۸ در حضور بای)
    //    براکت UWW بر اساس تشک چیده می‌شود: هر تشک یک‌چهارم جدول است و مقدماتی‌های همان تشک
    //    به یک‌هشتم‌های همان تشک می‌روند؛ مازادِ تشک به جای خالی سایر تشک‌ها (به ترتیب شماره).
    //    با برگزاری مسابقات، جریان برنده (گام ۱) جای این حدس را می‌گیرد و اتصال دقیق می‌شود.
    //    ⚠️ زوج‌ها از «راندهای موجود در داده» ساخته می‌شوند — نه زوج‌های ثابت MAIN_FLOW —
    //    چون در وزن‌های بدون راند ۱/۱۶، مقدماتی مستقیماً به یک‌هشتم می‌رود.
    const flowRounds = MAIN_FLOW.filter(r => matches.some(m => m.round === r));
    for (let i = 0; i + 1 < flowRounds.length; i++) {
        const prevRound = flowRounds[i];
        const nextRound = flowRounds[i + 1];
        const prevAll = matches.filter(m => m.round === prevRound);
        const nextAll = matches.filter(m => m.round === nextRound);
        if (!prevAll.length || !nextAll.length) continue;
        // اگر نسبت ۲:۱ برقرار بود، فالبک موقعیتی (گام ۲) قبلاً رسیدگی کرده است
        if (nextAll.length === Math.floor(prevAll.length / 2)) continue;

        const parents = nextAll
            .map(p => ({ p, open: 2 - childrenCount(nextOf, p.id) }))
            .filter(x => x.open > 0)
            .sort((a, b) => (a.p.number || 0) - (b.p.number || 0));
        if (!parents.length) continue;

        const pool = prevAll.filter(m => !nextOf.has(m.id));
        if (!pool.length) continue;

        // صف هر تشک: بازی‌های بدون‌اتصال همان تشک (به ترتیب شماره)
        const byMat = new Map();
        pool.forEach(m => {
            const key = m.mat || 'A';
            if (!byMat.has(key)) byMat.set(key, []);
            byMat.get(key).push(m);
        });
        byMat.forEach(list => list.sort((a, b) => (a.number || 0) - (b.number || 0)));

        parents.forEach(({ p, open }) => {
            const own = byMat.get(p.mat || 'A') || [];
            for (let k = 0; k < open; k++) {
                // اول صف تشک خود والد، بعد هر جای خالی دیگر از کل باقی‌مانده‌ها
                const child = own.length ? own.shift()
                    : (() => {
                        for (const list of byMat.values()) {
                            if (list.length) { const c = list.shift(); if (c) return c; }
                        }
                        return null;
                    })();
                if (!child) break;
                nextOf.set(child.id, p.id);
            }
        });
    }

    return nextOf;
}

function childrenCount(nextOf, parentId) {
    let n = 0;
    nextOf.forEach(pid => { if (pid === parentId) n++; });
    return n;
}

/** جمع‌آوری بازی‌های یک درخت از ریشه (والد قبل از فرزند — ترتیب برای map فرزندان مهم است) */
function collectTree(rootId, childrenOf, libGames) {
    const out = [];
    const seen = new Set();
    const walk = (id) => {
        if (seen.has(id)) return;
        seen.add(id);
        const g = libGames.get(id);
        if (!g) return;
        out.push(g);
        const kids = childrenOf.get(id) || [];
        kids.forEach(walk);
    };
    walk(rootId);
    return out;
}

/**
 * تابع اصلی: matches (خروجی wrestling.service) → درخت‌های flatTree
 * @returns {{ goldTree: Array, bronzeTrees: Array[], leftovers: Array }}
 */
function buildWrestlingBracket(matches) {
    if (!matches || !matches.length) {
        return { goldTree: [], bronzeTrees: [], leftovers: [] };
    }

    const matchesById = new Map(matches.map(m => [m.id, m]));
    const nextOf = buildNextMap(matches);

    // نقشهٔ فرزندان هر والد + مرتب‌سازی: فرزندی که برنده‌اش player1 والد است، بالا قرار می‌گیرد
    const childrenOf = new Map();
    nextOf.forEach((parentId, childId) => {
        if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
        childrenOf.get(parentId).push(childId);
    });
    childrenOf.forEach((kids, parentId) => {
        const parent = matchesById.get(parentId);
        const p1Key = parent ? playerKey(parent.red) : null;
        kids.sort((a, b) => {
            const ma = matchesById.get(a);
            const mb = matchesById.get(b);
            const aFirst = !!p1Key && winnerKeyOf(ma) === p1Key;
            const bFirst = !!p1Key && winnerKeyOf(mb) === p1Key;
            if (aFirst !== bFirst) return aFirst ? -1 : 1;
            const ra = ROUND_RANK[ma.round] !== undefined ? ROUND_RANK[ma.round] : 99;
            const rb = ROUND_RANK[mb.round] !== undefined ? ROUND_RANK[mb.round] : 99;
            if (ra !== rb) return ra - rb;
            return (ma.number || 0) - (mb.number || 0);
        });
    });

    // بازی‌های قالب کتابخانه
    const libGames = new Map(matches.map(m => [m.id, {
        id: m.id,
        next: nextOf.get(m.id),
        player1: toLibPlayer(m.red, m, 'red'),
        player2: toLibPlayer(m.blue, m, 'blue'),
        round: m.round,
        roundFa: m.roundFa || m.round,
        number: m.number,
        isFirstOfRound: false,
        match: m,
    }]));

    // درخت اصلی (طلا): ریشه = فینال ۱-۲
    const goldRoot = matches.find(m => m.round === 'Final 1-2');
    const goldTree = goldRoot
        ? markFirstOfRound(collectTree(goldRoot.id, childrenOf, libGames))
        : [];

    // درخت‌های برنز: ریشه‌ها = دیدارهای رده‌بندی ۳-۵ (رپشاژ به‌عنوان فرزند وصل می‌شود)
    const bronzeRoots = matches.filter(m => m.round === 'Final 3-5');
    const bronzeTrees = bronzeRoots.map(r => markFirstOfRound(collectTree(r.id, childrenOf, libGames)));

    // بازی‌های خارج از درخت‌ها (مثل مقدماتیِ جفت‌نشده)
    const inTree = new Set([...goldTree, ...bronzeTrees.flat()].map(g => g.id));
    const leftovers = matches
        .filter(m => !inTree.has(m.id))
        .sort((a, b) => {
            const ra = ROUND_RANK[a.round] !== undefined ? ROUND_RANK[a.round] : 99;
            const rb = ROUND_RANK[b.round] !== undefined ? ROUND_RANK[b.round] : 99;
            if (ra !== rb) return ra - rb;
            return (a.number || 0) - (b.number || 0);
        });

    return { goldTree, bronzeTrees, leftovers };
}

/** نخستین بازی هر راند در درخت را علامت می‌زند (برای چیپ عنوان راند) */
function markFirstOfRound(treeArr) {
    const seen = new Set();
    treeArr.forEach(g => {
        g.isFirstOfRound = !seen.has(g.round);
        seen.add(g.round);
    });
    return treeArr;
}

module.exports = { buildWrestlingBracket, playerKey, toLibPlayer, ROUND_RANK, MAIN_FLOW };
